import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useNotify } from 'react-admin';
import { useSignetStudio } from '../SignetStudioContext';
import { ACTIONS } from '../reducers/studioReducer';
import { apiUrl } from '../../../../dataProvider';
import { createMappingStructure } from '../utils/mappingUtils';

export const handleAPIError = (error, notify) => {
  const status = error.response?.status;
  if (status === 401 || status === 403) {
    window.location.replace('/#/login');
    return;
  }
  const message = error.response?.data?.message || 'An error occurred';
  notify(message, { type: 'error' });
  console.error('API Error:', error);
};

const THIN_WIDE_SHANKS = ['T', 'W'];
const RESTRICTED_MATERIALS = ['18KY','18KR', '18KW','10KY', '10KW']

/**
 * Hook for handling engraving-related API operations
 */
export const useEngravingAPI = () => {
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const { state, dispatch } = useSignetStudio();

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/signet-shape-config`);
    
      if (response?.data) {
        dispatch({ type: ACTIONS.SET_SHAPE_CONFIGS, payload: response.data?.data  || [] });
      }
    } catch (error) {
      handleAPIError(error, notify);
    }
  }, [notify, dispatch]);

  useEffect(() => {
    if (!state.shapeConfigs || state.shapeConfigs.length === 0) {
      fetchConfig();
    }
  }, [fetchConfig, state.shapeConfigs]);

  const fetchMaterials = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/signet-engraving-ring-materials`);
      if (response?.data) {
        const materials = Object.values(
          response.data?.data?.reduce((acc, material) => {
            if (!RESTRICTED_MATERIALS.includes(material.sku_code)) {
              acc[material.sku_code] = material;
            }
            return acc;
          }, {})
        );
        dispatch({ type: ACTIONS.SET_MATERIALS_LIST, payload: materials });

        // Create new mapping data with just materials
        const updatedMapping = createMappingStructure(null, null, null, null, null, materials);
        dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedMapping });
      }
    } catch (error) {
      handleAPIError(error, notify);
    }
  }, [notify, dispatch]);

  useEffect(() => {
    if (!state?.materialList || state?.materialList?.length === 0) {
      fetchMaterials();
    }
  }, [fetchMaterials, state?.materialList]);

  const fetchEngravingsList = useCallback(async () => {
    if (state?.engravingsList?.length > 0) return state?.engravingsList;

    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/signet-engravings`, {
        params: {
          'page[number]': 1,
          'page[size]': 1000,
          sort: 'id',
        },
      });
      const data = response.data.data;
      dispatch({ type: ACTIONS.SET_ENGRAVINGS_LIST, payload: data });
      return data;
    } catch (error) {
      handleAPIError(error, notify);
      return [];
    } finally {
      setLoading(false);
    }
  }, [notify, dispatch, state.engravingsList]);

  useEffect(() => {
    if (state?.engravingsList?.length === 0) {
      fetchEngravingsList();
    }
  }, [fetchEngravingsList]);

  const fetchEngravingDetails = useCallback(
    async (engravingId) => {
      if (!engravingId) return null;
      setLoading(true);

      try {
        const response = await axios.get(`${apiUrl}/signet-engraving-details/${engravingId}`);
        const data = response?.data?.data;

        if (data) {
          dispatch({ type: ACTIONS.SET_SELECTED_ENGRAVING, payload: data });
          
          // Create new mapping data with shapes and colors
          const updatedMapping = createMappingStructure(
            data.shapes_allowed,
            data.sizes_allowed,
            data.cerakote_colors_allowed,
            data.shanks_allowed.filter((shank) => THIN_WIDE_SHANKS.includes(shank.sku)),
            data.ring_materials_allowed,
            state.materialList,  
          );

          dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedMapping });
          dispatch({ type: ACTIONS.SET_FETCHED_ENGRAVING_ID, payload: engravingId });
        }

        return data;
      } catch (error) {
        handleAPIError(error, notify);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [notify, dispatch, state.materialList],
  );

  const fetchInlayMaterials = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/signet-inlay?page[number]=1&page[size]=1000`);
      if (response?.data) {
        const inlayMaterials = Object.values(
          response.data?.data?.reduce((acc, material) => {
            if (!RESTRICTED_MATERIALS.includes(material.sku_code)) {
              acc[material.sku_code] = material;
            }
            return acc;
          }, {})
        );
        dispatch({ type: ACTIONS.SET_INLAY_MATERIALS, payload: inlayMaterials });
      }
    } catch (error) {
      handleAPIError(error, notify);
    }
  }, [notify, dispatch]);

  useEffect(() => {
    if (!state?.inlayMaterials || state?.inlayMaterials?.length === 0) {
      fetchInlayMaterials();
    }
  }, [fetchInlayMaterials, state?.inlayMaterials]);

  return {
    loading,
    options: state.engravingsList,
    fetchEngravingsList,
    fetchEngravingDetails,
    fetchMaterials,
    fetchInlayMaterials,
    fetchConfig,
  };
};
